import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FileSearch, RefreshCcw } from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { reportsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { downloadExcelWorkbook } from '../../utils/exportExcel';

const todayKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatNumber = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return value ?? '-';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
};

const toSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatChartDate = (dateKey) => {
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateKey;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getGaugeTone = (percentage) => {
  if (percentage <= 10) {
    return {
      stroke: '#10b981',
      status: 'Healthy',
      statusClass: 'text-emerald-700 bg-emerald-100 border-emerald-200',
    };
  }

  if (percentage <= 35) {
    return {
      stroke: '#f59e0b',
      status: 'Watch',
      statusClass: 'text-amber-700 bg-amber-100 border-amber-200',
    };
  }

  return {
    stroke: '#ef4444',
    status: 'Critical',
    statusClass: 'text-rose-700 bg-rose-100 border-rose-200',
  };
};

const STORE_ALIAS_METADATA = {
  kathmandu: {
    rowKey: 'kathmandu',
    label: 'Kathmandu',
    shortLabel: 'KATHMANDU',
    orderedKey: 'ktmOrdered',
    arrivedKey: 'ktmArrived',
  },
  kumaripati: {
    rowKey: 'kumaripati',
    label: 'Lalitpur',
    shortLabel: 'LALITPUR',
    orderedKey: 'lptOrdered',
    arrivedKey: 'lptArrived',
  },
};

const formatFileToken = (value) => String(value ?? 'all-stores')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'all-stores';
const FINAL_REPORT_SESSION_CACHE_PREFIX = 'final-report-cache::v3::';
const getFinalReportSessionCacheTtlMs = (dateKey) => (
  dateKey === todayKey()
    ? 15 * 60 * 1000
    : 15 * 60 * 1000
);
const getFinalReportSessionCacheMaxAgeMs = (dateKey) => (
  dateKey === todayKey()
    ? 12 * 60 * 60 * 1000
    : 30 * 24 * 60 * 60 * 1000
);

const buildFinalReportSessionCacheKey = (dateKey, storeId) => (
  `${FINAL_REPORT_SESSION_CACHE_PREFIX}${dateKey || todayKey()}::${storeId || 'all'}`
);

const readFinalReportSessionCacheEntry = (dateKey, storeId, { allowStale = false } = {}) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;

  try {
    const raw = window.sessionStorage.getItem(buildFinalReportSessionCacheKey(dateKey, storeId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt || !parsed?.payload?.report) {
      window.sessionStorage.removeItem(buildFinalReportSessionCacheKey(dateKey, storeId));
      return null;
    }

    const ageMs = Date.now() - parsed.savedAt;
    const ttlMs = getFinalReportSessionCacheTtlMs(dateKey || todayKey());
    const maxAgeMs = getFinalReportSessionCacheMaxAgeMs(dateKey || todayKey());
    if (ageMs > maxAgeMs) {
      window.sessionStorage.removeItem(buildFinalReportSessionCacheKey(dateKey, storeId));
      return null;
    }

    if (!allowStale && ageMs > ttlMs) {
      return null;
    }

    return {
      ...parsed,
      isStale: ageMs > ttlMs,
    };
  } catch (_) {
    return null;
  }
};

const writeFinalReportSessionCache = (dateKey, storeId, payload) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  try {
    window.sessionStorage.setItem(
      buildFinalReportSessionCacheKey(dateKey, storeId),
      JSON.stringify({
        savedAt: Date.now(),
        payload,
      })
    );
  } catch (_) {
    // Ignore session cache write issues.
  }
};

const gaugePoint = (cx, cy, radius, angleDegrees) => {
  const radians = (Math.PI / 180) * angleDegrees;
  return {
    x: cx + (radius * Math.cos(radians)),
    y: cy - (radius * Math.sin(radians)),
  };
};

const AngularGauge = ({ value = 0, color = '#ef4444' }) => {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
  const [animatedValue, setAnimatedValue] = useState(0);
  const animatedValueRef = useRef(0);
  const animationFrameRef = useRef(null);
  // Top semicircle arc, left (0%) to right (100%)
  const arcPath = 'M 34 126 A 86 86 0 0 1 206 126';

  useEffect(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const startValue = animatedValueRef.current;
    const targetValue = safeValue;
    const durationMs = 1100;
    const easeOutCubic = (progress) => 1 - ((1 - progress) ** 3);
    const animationStart = performance.now();

    const step = (timestamp) => {
      const progress = Math.min((timestamp - animationStart) / durationMs, 1);
      const nextValue = startValue + ((targetValue - startValue) * easeOutCubic(progress));
      animatedValueRef.current = nextValue;
      setAnimatedValue(nextValue);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      }
    };

    animationFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [safeValue]);

  const pointerAngle = 180 - ((animatedValue / 100) * 180);
  const pointer = gaugePoint(120, 126, 86, pointerAngle);
  const progressStroke = safeValue <= 10
    ? '#16a34a'
    : safeValue <= 30
      ? 'url(#angularGaugeBandTwo)'
      : safeValue <= 40
        ? 'url(#angularGaugeBandThree)'
        : '#ef4444';

  return (
    <svg viewBox="0 0 240 170" className="h-full w-full">
      <defs>
        <linearGradient id="angularGaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#16a34a" />
          <stop offset="10%" stopColor="#22c55e" />
          <stop offset="24%" stopColor="#facc15" />
          <stop offset="35%" stopColor="#eab308" />
          <stop offset="46%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#ef4444" />
        </linearGradient>
        <linearGradient id="angularGaugeBandTwo" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#16a34a" />
          <stop offset="50%" stopColor="#65a30d" />
          <stop offset="100%" stopColor="#eab308" />
        </linearGradient>
        <linearGradient id="angularGaugeBandThree" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#16a34a" />
          <stop offset="8%" stopColor="#65a30d" />
          <stop offset="16%" stopColor="#eab308" />
          <stop offset="24%" stopColor="#f59e0b" />
          <stop offset="30%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#ef4444" />
        </linearGradient>
        <linearGradient id="angularGaugeWater" gradientUnits="userSpaceOnUse" x1="26" y1="80" x2="214" y2="80">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.08" />
          <stop offset="24%" stopColor="#e0f2fe" stopOpacity="0.3" />
          <stop offset="42%" stopColor="#ffffff" stopOpacity="0.82" />
          <stop offset="55%" stopColor="#dbeafe" stopOpacity="0.66" />
          <stop offset="74%" stopColor="#ffffff" stopOpacity="0.34" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.08" />
          <animateTransform
            attributeName="gradientTransform"
            type="translate"
            values="-90 0;90 0;-90 0"
            dur="6.4s"
            repeatCount="indefinite"
          />
        </linearGradient>
        <linearGradient id="angularGaugePasteRim" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#052e16" stopOpacity="0.52" />
          <stop offset="40%" stopColor="#7c2d12" stopOpacity="0.38" />
          <stop offset="100%" stopColor="#7f1d1d" stopOpacity="0.5" />
        </linearGradient>
        <linearGradient id="angularGaugeTrack" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="50%" stopColor="#e2e8f0" />
          <stop offset="100%" stopColor="#c5d0de" />
        </linearGradient>
        <linearGradient id="angularGaugeGloss" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.74" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="angularGaugeCornerShine" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.88" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <filter id="softShadow" x="-20%" y="-20%" width="140%" height="180%">
          <feDropShadow dx="0" dy="1.2" stdDeviation="1.4" floodColor="#0f172a" floodOpacity="0.18" />
        </filter>
        <filter id="deepShadow" x="-30%" y="-30%" width="170%" height="220%">
          <feDropShadow dx="0.5" dy="0.8" stdDeviation="1.2" floodColor="#0f172a" floodOpacity="0.2" />
          <feDropShadow dx="-0.4" dy="-0.6" stdDeviation="1" floodColor="#ffffff" floodOpacity="0.46" />
        </filter>
        <filter id="pasteDepth" x="-35%" y="-35%" width="200%" height="260%">
          <feDropShadow dx="0.4" dy="1.1" stdDeviation="1.3" floodColor="#020617" floodOpacity="0.34" />
          <feDropShadow dx="-0.2" dy="-0.5" stdDeviation="0.8" floodColor="#ffffff" floodOpacity="0.52" />
        </filter>
        <filter id="neumorphInner" x="-30%" y="-30%" width="170%" height="220%">
          <feDropShadow dx="-1.2" dy="-1.2" stdDeviation="1.4" floodColor="#475569" floodOpacity="0.24" />
          <feDropShadow dx="1.2" dy="1.4" stdDeviation="1.3" floodColor="#ffffff" floodOpacity="0.8" />
        </filter>
      </defs>

      <path
        d={arcPath}
        fill="none"
        stroke="#8da0b8"
        strokeWidth="18"
        strokeLinecap="round"
        pathLength={100}
        opacity="0.42"
        transform="translate(-0.6 -1.2)"
      />
      <path
        d={arcPath}
        fill="none"
        stroke="#ffffff"
        strokeWidth="18"
        strokeLinecap="round"
        pathLength={100}
        opacity="0.86"
        transform="translate(0.8 1.5)"
      />
      <path
        d={arcPath}
        fill="none"
        stroke="#d4deea"
        strokeWidth="16.5"
        strokeLinecap="round"
        pathLength={100}
        opacity="0.85"
      />
      <path
        d={arcPath}
        fill="none"
        stroke="url(#angularGaugeTrack)"
        strokeWidth="15.5"
        strokeLinecap="round"
        pathLength={100}
        filter="url(#neumorphInner)"
      />
      <path
        d={arcPath}
        fill="none"
        stroke="url(#angularGaugePasteRim)"
        strokeWidth="14.9"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={`${animatedValue} 100`}
        strokeDashoffset="0"
        transform="translate(0 0.8)"
        opacity="0.64"
      />
      <path
        d={arcPath}
        fill="none"
        stroke={progressStroke}
        strokeWidth="13.8"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={`${animatedValue} 100`}
        strokeDashoffset="0"
        filter="url(#pasteDepth)"
      />
      <path
        d={arcPath}
        fill="none"
        stroke="#ffffff"
        strokeWidth="3"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={`${animatedValue} 100`}
        strokeDashoffset="0"
        transform="translate(-0.1 -0.9)"
        opacity="0.45"
      />
      <path
        d={arcPath}
        fill="none"
        stroke="#0f172a"
        strokeWidth="3.4"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={`${animatedValue} 100`}
        strokeDashoffset="0"
        transform="translate(0.1 1.2)"
        opacity="0.14"
      />
      <path
        d={arcPath}
        fill="none"
        stroke={progressStroke}
        strokeWidth="11.4"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={`${animatedValue} 100`}
        strokeDashoffset="0"
        filter="url(#deepShadow)"
      />
      <path
        d={arcPath}
        fill="none"
        stroke="url(#angularGaugeWater)"
        strokeWidth="8.2"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={`${animatedValue} 100`}
        strokeDashoffset="0"
        opacity="0.94"
      />
      <path
        d={arcPath}
        fill="none"
        stroke="url(#angularGaugeGloss)"
        strokeWidth="3.7"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={`${animatedValue} 100`}
      />
      <ellipse
        cx="199"
        cy="91"
        rx="17"
        ry="7"
        transform="rotate(-24 199 91)"
        fill="url(#angularGaugeCornerShine)"
        opacity="0.66"
      />

      <circle
        cx={pointer.x}
        cy={pointer.y}
        r="6"
        fill="#ffffff"
        stroke={color}
        strokeWidth="3"
        filter="url(#softShadow)"
      />
      <circle
        cx={pointer.x}
        cy={pointer.y}
        r="2"
        fill={color}
      />

      <text x="24" y="150" fill="#64748b" fontSize="10.5" fontWeight="600">0%</text>
      <text x="112" y="150" fill="#64748b" fontSize="10.5" fontWeight="600">50%</text>
      <text x="206" y="150" fill="#64748b" fontSize="10.5" fontWeight="600">100%</text>
    </svg>
  );
};

const DayAmountTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const purchased = payload.find((entry) => entry.dataKey === 'purchasedAmount');
  const sold = payload.find((entry) => entry.dataKey === 'soldAmount');

  return (
    <div className="rounded-xl border border-slate-200 bg-white/95 px-3 py-2 shadow-xl backdrop-blur">
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <div className="mt-2 space-y-1 text-xs">
        <div className="flex items-center justify-between gap-3">
          <span className="font-medium text-sky-700">Purchased Amt</span>
          <span className="font-semibold text-slate-900">{formatNumber(purchased?.value ?? 0)}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="font-medium text-amber-700">Sold Amt</span>
          <span className="font-semibold text-slate-900">{formatNumber(sold?.value ?? 0)}</span>
        </div>
      </div>
    </div>
  );
};

const normalizePriority = (value) => {
  const raw = String(value ?? '').trim().toUpperCase();
  if (!raw || raw === 'TOTAL') return null;
  const compact = raw.replace(/[\s_-]+/g, '');
  const match = /^P?(\d+)$/.exec(compact);
  if (!match) return raw;
  const numeric = Number(match[1]);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return `P${numeric}`;
};

const buildTable1Rows = (rows = []) => {
  const grouped = new Map();

  rows.forEach((row) => {
    const priority = normalizePriority(row?.priority);
    if (!priority) return;
    const incoming = {
      kathmandu: {
        totalPublished: toSafeNumber(row?.kathmandu?.totalPublished),
        outOfStock: toSafeNumber(row?.kathmandu?.outOfStock),
      },
      kumaripati: {
        totalPublished: toSafeNumber(row?.kumaripati?.totalPublished),
        outOfStock: toSafeNumber(row?.kumaripati?.outOfStock),
      },
    };

    if (!grouped.has(priority)) {
      grouped.set(priority, {
        priority,
        kathmandu: { ...incoming.kathmandu, outOfStockPct: 0 },
        kumaripati: { ...incoming.kumaripati, outOfStockPct: 0 },
      });
      return;
    }

    const bucket = grouped.get(priority);
    bucket.kathmandu.totalPublished = Math.max(bucket.kathmandu.totalPublished, incoming.kathmandu.totalPublished);
    bucket.kathmandu.outOfStock = Math.max(bucket.kathmandu.outOfStock, incoming.kathmandu.outOfStock);
    bucket.kumaripati.totalPublished = Math.max(bucket.kumaripati.totalPublished, incoming.kumaripati.totalPublished);
    bucket.kumaripati.outOfStock = Math.max(bucket.kumaripati.outOfStock, incoming.kumaripati.outOfStock);
  });

  const sorted = Array.from(grouped.values()).sort((left, right) => {
    const leftNum = Number(String(left.priority).replace(/^P/, ''));
    const rightNum = Number(String(right.priority).replace(/^P/, ''));
    if (Number.isFinite(leftNum) && Number.isFinite(rightNum)) return leftNum - rightNum;
    return String(left.priority).localeCompare(String(right.priority));
  });

  sorted.forEach((row) => {
    row.kathmandu.outOfStock = Math.min(row.kathmandu.outOfStock, row.kathmandu.totalPublished);
    row.kumaripati.outOfStock = Math.min(row.kumaripati.outOfStock, row.kumaripati.totalPublished);
    row.kathmandu.outOfStockPct = row.kathmandu.totalPublished > 0
      ? Number(((row.kathmandu.outOfStock / row.kathmandu.totalPublished) * 100).toFixed(2))
      : 0;
    row.kumaripati.outOfStockPct = row.kumaripati.totalPublished > 0
      ? Number(((row.kumaripati.outOfStock / row.kumaripati.totalPublished) * 100).toFixed(2))
      : 0;
  });

  const total = sorted.reduce((acc, row) => ({
    kathmandu: {
      totalPublished: acc.kathmandu.totalPublished + toSafeNumber(row.kathmandu?.totalPublished),
      outOfStock: acc.kathmandu.outOfStock + toSafeNumber(row.kathmandu?.outOfStock),
    },
    kumaripati: {
      totalPublished: acc.kumaripati.totalPublished + toSafeNumber(row.kumaripati?.totalPublished),
      outOfStock: acc.kumaripati.outOfStock + toSafeNumber(row.kumaripati?.outOfStock),
    },
  }), {
    kathmandu: { totalPublished: 0, outOfStock: 0 },
    kumaripati: { totalPublished: 0, outOfStock: 0 },
  });

  const totalRow = {
    priority: 'Total',
    isTotal: true,
    kathmandu: {
      totalPublished: total.kathmandu.totalPublished,
      outOfStock: total.kathmandu.outOfStock,
      outOfStockPct: total.kathmandu.totalPublished > 0
        ? Number(((total.kathmandu.outOfStock / total.kathmandu.totalPublished) * 100).toFixed(2))
        : 0,
    },
    kumaripati: {
      totalPublished: total.kumaripati.totalPublished,
      outOfStock: total.kumaripati.outOfStock,
      outOfStockPct: total.kumaripati.totalPublished > 0
        ? Number(((total.kumaripati.outOfStock / total.kumaripati.totalPublished) * 100).toFixed(2))
        : 0,
    },
  };

  return [...sorted, totalRow];
};

const formatCoverageMetricValue = (metricKey, value) => (
  metricKey === 'outOfStockPct'
    ? `${formatNumber(value)}%`
    : formatNumber(value)
);

const CoverageSummaryCard = ({
  toneLabel,
  toneClassName,
  toneDotClassName,
  displayValue,
  metricKey,
  helperText = '',
  breakdownRows = [],
}) => (
  <div className="card relative overflow-hidden border border-slate-200/70 bg-gradient-to-br from-white via-slate-50 to-slate-100 shadow-[0_10px_20px_rgba(15,23,42,0.08)]">
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.55),transparent_58%)]" />
    <div className={`relative z-10 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${toneClassName}`}>
      <span className={`h-2 w-2 rounded-full ${toneDotClassName}`} />
      {toneLabel}
    </div>
    <p className="relative z-10 mt-2 text-3xl font-bold leading-none text-slate-900">{displayValue}</p>
    {helperText ? <p className="relative z-10 mt-2 text-xs text-slate-500">{helperText}</p> : null}
    <div className="relative z-10 mt-4 space-y-2 border-t border-slate-200/70 pt-3">
      {breakdownRows.map((row) => (
        <div key={row.alias} className="flex items-center justify-between gap-3 text-[12px]">
          <span className="min-w-0 truncate font-medium text-slate-600">{row.label}</span>
          <span className="shrink-0 font-semibold text-slate-900">
            {formatCoverageMetricValue(metricKey, row.value)}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const FinalReport = ({
  headerTitle = 'Final Report',
  headerDescription = '',
  onFilterStateChange = null,
} = {}) => {
  const { hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const canViewPage = hasFeatureAccess('final_report.view');
  const selectedStoreId = String(selectedStore?.storeInfoId || selectedStore?.store_info_id || selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || '');
  const selectedStoreDisplayName = selectedStore?.name || selectedStore?.storeName || selectedStore?.store_code || selectedStore?.storeCode || 'Selected Store';
  const initialCachedEntry = readFinalReportSessionCacheEntry(todayKey(), selectedStoreId, { allowStale: true });
  const [date, setDate] = useState(todayKey());
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(() => initialCachedEntry?.payload?.report || null);
  const [trendRows, setTrendRows] = useState(() => initialCachedEntry?.payload?.trendRows || []);
  const [activeTableScrollId, setActiveTableScrollId] = useState(null);
  const reportRequestRef = useRef(0);
  const effectiveStoreId = selectedStoreId;

  const buildReportParams = useCallback((targetDate, targetStoreId) => ({
    date: targetDate,
    ...(targetStoreId && targetStoreId !== 'all' ? { storeId: targetStoreId } : {}),
  }), []);

  const loadReport = useCallback(async (
    targetDate,
    targetStoreId,
    { showLoader = true, preserveExistingOnError = false } = {}
  ) => {
    const requestId = ++reportRequestRef.current;
    if (showLoader) {
      setLoading(true);
    }
    try {
      const response = await reportsAPI.getFinalReport(buildReportParams(targetDate, targetStoreId));
      if (reportRequestRef.current === requestId) {
        const payload = response.data?.data || null;
        const nextTrendRows = (payload?.trend?.rows || []).map((row) => ({
          date: row.date,
          label: formatChartDate(row.date),
          purchasedAmount: toSafeNumber(row.purchasedAmount),
          soldAmount: toSafeNumber(row.soldAmount),
        }));

        setReport(payload);
        setTrendRows(nextTrendRows);
        writeFinalReportSessionCache(targetDate, targetStoreId, {
          report: payload,
          trendRows: nextTrendRows,
        });
      }
    } catch (error) {
      if (reportRequestRef.current === requestId) {
        toast.error(error?.response?.data?.message || 'Failed to load final report');
        if (!preserveExistingOnError) {
          setReport(null);
          setTrendRows([]);
        }
      }
    } finally {
      if (reportRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [buildReportParams]);

  useEffect(() => {
    if (!canViewPage) return;
    if (!effectiveStoreId) {
      setReport(null);
      setTrendRows([]);
      return;
    }

    const cachedEntry = readFinalReportSessionCacheEntry(date, effectiveStoreId, { allowStale: true });
    if (cachedEntry?.payload?.report) {
      setReport(cachedEntry.payload.report);
      setTrendRows(cachedEntry.payload.trendRows || []);
      if (!cachedEntry.isStale) {
        return undefined;
      }
    }

    loadReport(date, effectiveStoreId, {
      showLoader: !cachedEntry?.payload?.report,
      preserveExistingOnError: Boolean(cachedEntry?.payload?.report),
    });
    return undefined;
  }, [canViewPage, date, effectiveStoreId, loadReport]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-final-report-scroll-shell="true"]')) return;
      setActiveTableScrollId(null);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  const table1Rows = useMemo(() => buildTable1Rows(report?.table1?.rows || []), [report?.table1?.rows]);
  const table2Rows = useMemo(() => report?.table2?.rows || [], [report?.table2?.rows]);
  const fruitsRows = useMemo(() => report?.fruitsVegetable?.rows || [], [report?.fruitsVegetable?.rows]);
  const table3Rows = useMemo(() => report?.table3?.rows || [], [report?.table3?.rows]);
  const reportStores = useMemo(() => report?.stores || {}, [report?.stores]);
  const availableReportAliases = useMemo(
    () => Object.keys(STORE_ALIAS_METADATA).filter((alias) => reportStores?.[alias]?.id),
    [reportStores]
  );
  const selectedStoreAlias = useMemo(() => {
    if (!effectiveStoreId) return null;
    const match = Object.entries(reportStores).find(
      ([, store]) => String(store?.id || '') === String(effectiveStoreId)
    );
    return match?.[0] || null;
  }, [effectiveStoreId, reportStores]);
  const visibleStoreAliases = useMemo(() => {
    if (selectedStoreAlias) return [selectedStoreAlias];
    return availableReportAliases.length ? availableReportAliases : Object.keys(STORE_ALIAS_METADATA);
  }, [availableReportAliases, selectedStoreAlias]);
  const visibleStoreColumns = useMemo(
    () => visibleStoreAliases.map((alias) => ({
      alias,
      ...STORE_ALIAS_METADATA[alias],
      label: STORE_ALIAS_METADATA[alias]?.label || reportStores?.[alias]?.name || alias,
    })),
    [reportStores, visibleStoreAliases]
  );
  const showCombinedColumns = visibleStoreColumns.length > 1;
  const metricsTableMinWidth = useMemo(() => {
    const particularColumnWidth = 100;
    const eachStoreMetricWidth = 100;
    const totalColumnWidth = showCombinedColumns ? 120 : 0;
    const computed = particularColumnWidth + (visibleStoreColumns.length * eachStoreMetricWidth) + totalColumnWidth;
    return Math.max(600, computed);
  }, [showCombinedColumns, visibleStoreColumns.length]);
  const selectedStoreName = useMemo(() => {
    if (!effectiveStoreId) return 'Select store from header';

    return Object.values(reportStores).find((store) => String(store?.id || '') === String(effectiveStoreId))?.name
      || selectedStoreDisplayName
      || `Store ${effectiveStoreId}`;
  }, [effectiveStoreId, reportStores, selectedStoreDisplayName]);

  useEffect(() => {
    if (typeof onFilterStateChange !== 'function') return;
    onFilterStateChange({
      date,
      storeId: effectiveStoreId,
      selectedStoreName,
    });
  }, [date, effectiveStoreId, onFilterStateChange, selectedStoreName]);

  const coverageStoreSummaries = useMemo(() => {
    const totalRow = table1Rows.find((row) => row.isTotal);

    return visibleStoreColumns.map((column) => {
      const sourceRows = totalRow ? [totalRow] : table1Rows;
      const totalPublished = totalRow
        ? toSafeNumber(totalRow[column.rowKey]?.totalPublished)
        : sourceRows.reduce(
            (sum, row) => sum + toSafeNumber(row[column.rowKey]?.totalPublished),
            0
          );
      const outOfStock = totalRow
        ? toSafeNumber(totalRow[column.rowKey]?.outOfStock)
        : sourceRows.reduce(
            (sum, row) => sum + toSafeNumber(row[column.rowKey]?.outOfStock),
            0
          );
      const inStock = Math.max(0, totalPublished - outOfStock);
      const outOfStockPct = totalPublished > 0
        ? Number(((outOfStock / totalPublished) * 100).toFixed(2))
        : 0;

      return {
        alias: column.alias,
        label: column.label,
        totalPublished,
        outOfStock,
        inStock,
        outOfStockPct,
      };
    });
  }, [table1Rows, visibleStoreColumns]);

  const priorityCoverageSummary = useMemo(() => {
    const totalPublished = coverageStoreSummaries.reduce((sum, store) => sum + store.totalPublished, 0);
    const outOfStock = coverageStoreSummaries.reduce((sum, store) => sum + store.outOfStock, 0);
    const inStock = Math.max(0, totalPublished - outOfStock);
    const outOfStockPct = totalPublished > 0
      ? Number(((outOfStock / totalPublished) * 100).toFixed(2))
      : 0;

    return {
      totalPublished,
      outOfStock,
      inStock,
      outOfStockPct,
    };
  }, [coverageStoreSummaries]);
  const coverageCardHelperText = coverageStoreSummaries.length > 1
    ? 'Combined total across the selected stores.'
    : 'Store total.';
  const gaugeTone = useMemo(
    () => getGaugeTone(priorityCoverageSummary.outOfStockPct),
    [priorityCoverageSummary.outOfStockPct]
  );
  const trendLatest = useMemo(
    () => (trendRows.length ? trendRows[trendRows.length - 1] : null),
    [trendRows]
  );
  const isRefreshing = loading;

  const getTableShellProps = useCallback((tableId, { compact = false, noHeightLimit = false } = {}) => {
    const isActive = activeTableScrollId === tableId;
    const shellClass = compact ? 'table-scroll-shell-compact' : 'table-scroll-shell';
    const lockClass = (compact || noHeightLimit) ? '' : (isActive ? 'rounded-md ring-2 ring-slate-300' : 'table-scroll-shell--locked');
    return {
      className: `${shellClass} w-full overflow-x-auto ${noHeightLimit ? 'max-h-none' : ''} ${lockClass}`.trim(),
      onClick: () => setActiveTableScrollId(tableId),
      onFocus: () => setActiveTableScrollId(tableId),
      'data-final-report-scroll-shell': 'true',
      role: 'region',
      tabIndex: 0,
      'aria-label': 'Scrollable report table (click to activate table scroll)',
    };
  }, [activeTableScrollId]);

  const exportExcel = () => {
    const fileStoreToken = formatFileToken(selectedStoreName);
    downloadExcelWorkbook({
      fileName: `final-report-${fileStoreToken}-${date}.xlsx`,
      sheets: [
        {
          sheetName: 'Priority Coverage',
          columns: [
            { header: 'Priority', key: 'priority', width: 14 },
            ...visibleStoreColumns.flatMap((column) => ([
              { header: `Published ${column.shortLabel}`, key: `${column.alias}Published`, width: 16 },
              { header: `Out of Stock ${column.shortLabel}`, key: `${column.alias}OutOfStock`, width: 18 },
              { header: `Out of Stock % ${column.shortLabel}`, key: `${column.alias}OutOfStockPct`, width: 18 },
            ])),
          ],
          rows: table1Rows.map((row) => ({
            priority: row.priority,
            ...visibleStoreColumns.reduce((accumulator, column) => ({
              ...accumulator,
              [`${column.alias}Published`]: row[column.rowKey]?.totalPublished ?? 0,
              [`${column.alias}OutOfStock`]: row[column.rowKey]?.outOfStock ?? 0,
              [`${column.alias}OutOfStockPct`]: row[column.rowKey]?.outOfStockPct ?? 0,
            }), {}),
          })),
        },
        {
          sheetName: 'KPI Movement',
          columns: [
            { header: 'Particular', key: 'particular', width: 30 },
            ...visibleStoreColumns.map((column) => ({
              header: column.label,
              key: column.rowKey,
              width: 10,
            })),
            ...(showCombinedColumns ? [{ header: 'Total', key: 'total', width: 14 }] : []),
          ],
          rows: table2Rows,
        },
        {
          sheetName: 'Fruits and Vegetable',
          columns: [
            { header: 'Particular', key: 'particular', width: 30 },
            ...visibleStoreColumns.map((column) => ({
              header: column.label,
              key: column.rowKey,
              width: 10,
            })),
            ...(showCombinedColumns ? [{ header: 'Total', key: 'total', width: 14 }] : []),
          ],
          rows: fruitsRows,
        },
        {
          sheetName: 'Ordered vs Arrived',
          columns: [
            { header: 'Item Priority', key: 'itemPriority', width: 16 },
            ...visibleStoreColumns.flatMap((column) => ([
              { header: `${column.shortLabel} SKUs Ordered`, key: column.orderedKey, width: 18 },
              { header: `${column.shortLabel} SKUs Arrived`, key: column.arrivedKey, width: 18 },
            ])),
          ],
          rows: table3Rows,
        },
      ],
    });
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Final Report</h1>
        <div className="card">
          <p className="text-gray-500"></p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight text-slate-900">{headerTitle}</h1>
          {headerDescription ? (
            <p className="mt-1 text-sm text-slate-600">
              {headerDescription}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="label">Date</label>
            <input
              type="date"
              className="input min-w-[160px] text-sm"
              value={date}
              max={todayKey()}
              onChange={(event) => setDate(event.target.value)}
            />
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            disabled={!effectiveStoreId}
            onClick={async () => {
              await loadReport(date, effectiveStoreId, { showLoader: true });
            }}
          >
            <RefreshCcw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <ExportButton onClick={exportExcel} size="sm" />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <CoverageSummaryCard
          toneLabel="Total Published"
          toneClassName="border-slate-200 bg-slate-100/80 text-slate-700"
          toneDotClassName="bg-slate-500/80"
          displayValue={formatNumber(priorityCoverageSummary.totalPublished)}
          metricKey="totalPublished"
          helperText={coverageCardHelperText}
          breakdownRows={coverageStoreSummaries.map((store) => ({
            alias: store.alias,
            label: store.label,
            value: store.totalPublished,
          }))}
        />
        <CoverageSummaryCard
          toneLabel="Out of Stock"
          toneClassName="border-rose-200 bg-rose-50/85 text-rose-700"
          toneDotClassName="bg-rose-500"
          displayValue={formatNumber(priorityCoverageSummary.outOfStock)}
          metricKey="outOfStock"
          helperText={coverageCardHelperText}
          breakdownRows={coverageStoreSummaries.map((store) => ({
            alias: store.alias,
            label: store.label,
            value: store.outOfStock,
          }))}
        />
        <CoverageSummaryCard
          toneLabel="In Stock"
          toneClassName="border-emerald-200 bg-emerald-50/85 text-emerald-700"
          toneDotClassName="bg-emerald-500"
          displayValue={formatNumber(priorityCoverageSummary.inStock)}
          metricKey="inStock"
          helperText={coverageCardHelperText}
          breakdownRows={coverageStoreSummaries.map((store) => ({
            alias: store.alias,
            label: store.label,
            value: store.inStock,
          }))}
        />
        <CoverageSummaryCard
          toneLabel="OOS Ratio"
          toneClassName="border-amber-200 bg-amber-50/85 text-amber-700"
          toneDotClassName="bg-amber-500"
          displayValue={`${formatNumber(priorityCoverageSummary.outOfStockPct)}%`}
          metricKey="outOfStockPct"
          helperText={coverageCardHelperText}
          breakdownRows={coverageStoreSummaries.map((store) => ({
            alias: store.alias,
            label: store.label,
            value: store.outOfStockPct,
          }))}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
        <div className="card relative overflow-hidden border border-white/70 bg-white/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_20px_34px_rgba(15,23,42,0.12)] backdrop-blur-xl">
          <div className="pointer-events-none absolute -top-8 left-8 h-24 w-40 rounded-full bg-white/55 blur-2xl" />
          <div className="pointer-events-none absolute -right-8 bottom-0 h-28 w-44 rounded-full bg-sky-100/35 blur-2xl" />
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Out of Stock Performance</h2>
              <p className="mt-1 text-xs text-slate-500">
                {effectiveStoreId ? `(${selectedStoreName})` : '(select store)'}
              </p>
            </div>
            <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${gaugeTone.statusClass}`}>
              {gaugeTone.status}
            </span>
          </div>
          <div className="mt-3 rounded-lg border border-white/70 bg-gradient-to-br from-white/80 via-slate-100/60 to-slate-200/45 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_10px_18px_rgba(15,23,42,0.08)] backdrop-blur-md">
            <div className="relative h-[230px]">
              <AngularGauge value={priorityCoverageSummary.outOfStockPct} color={gaugeTone.stroke} />
              <div className="pointer-events-none absolute inset-x-0 top-[44%] flex flex-col items-center">
                <p className="text-4xl font-bold leading-none text-slate-900">{formatNumber(priorityCoverageSummary.outOfStockPct)}%</p>
                <p className="mt-1 text-[11px] font-semibold tracking-[0.12em] text-slate-500">OUT OF STOCK</p>
              </div>
            </div>
            <div className="mt-1 grid grid-cols-3 text-[11px] font-semibold text-slate-500">
              <span className="text-emerald-600">Healthy</span>
              <span className="text-center text-amber-600">Watch</span>
              <span className="text-right text-rose-600">Critical</span>
            </div>
          </div>
          <div className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p className="text-xs font-medium text-slate-600">
              {priorityCoverageSummary.outOfStock > 0
                ? `${formatNumber(priorityCoverageSummary.outOfStock)} items are currently out of stock.`
                : 'No out-of-stock items found for this day.'}
            </p>
          </div>
        </div>

        <div className="card relative overflow-hidden border border-white/70 bg-white/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_20px_34px_rgba(15,23,42,0.12)] backdrop-blur-xl">
          <div className="pointer-events-none absolute -top-10 right-10 h-28 w-48 rounded-full bg-white/60 blur-2xl" />
          <div className="pointer-events-none absolute left-6 top-20 h-20 w-32 rounded-full bg-amber-100/35 blur-2xl" />
          <h2 className="text-base font-semibold text-slate-900">Fruits & Vegetables: Purchase Amount vs Sold Amount</h2>
          <p className="mt-1 text-xs text-slate-500">
            Last 7 days daily amount trend{effectiveStoreId ? ` • ${selectedStoreName}` : ''}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-1 font-medium text-sky-700">
              <span className="h-2 w-2 rounded-full bg-sky-500" />
              Purchased Amount
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 font-medium text-amber-700">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              Sold Amount
            </span>
            {trendLatest ? (
              <span className="ml-auto text-slate-500">
                Latest: {trendLatest.label}
              </span>
            ) : null}
          </div>

          <div className="mt-2 h-[300px] rounded-lg border border-white/65 bg-gradient-to-br from-white/70 to-slate-100/50 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_10px_18px_rgba(15,23,42,0.08)] backdrop-blur-md">
            {loading && !trendRows.length ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-500">
                Loading 7-day trend...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendRows} margin={{ top: 14, right: 14, left: 4, bottom: 6 }}>
                  <defs>
                    <linearGradient id="purchasedLineStroke" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#0284c7" />
                      <stop offset="100%" stopColor="#0ea5e9" />
                    </linearGradient>
                    <linearGradient id="soldLineStroke" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#d97706" />
                      <stop offset="100%" stopColor="#f59e0b" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#dbe4ee" />
                  <XAxis
                    dataKey="label"
                    stroke="#64748b"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#64748b"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <RechartsTooltip content={<DayAmountTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="purchasedAmount"
                    stroke="url(#purchasedLineStroke)"
                    strokeWidth={3.6}
                    dot={{ r: 3, strokeWidth: 1.8, fill: '#ffffff' }}
                    activeDot={{ r: 5.5, stroke: '#0284c7', strokeWidth: 2.2, fill: '#ffffff' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="soldAmount"
                    stroke="url(#soldLineStroke)"
                    strokeWidth={4}
                    dot={{ r: 3, strokeWidth: 1.8, fill: '#ffffff' }}
                    activeDot={{ r: 5.5, stroke: '#d97706', strokeWidth: 2.2, fill: '#ffffff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="mb-3 text-base font-semibold text-slate-900">Priority Coverage</h2>
        <div {...getTableShellProps('table1')}>
          <table className={`${showCombinedColumns ? 'min-w-[980px]' : 'min-w-[560px]'} border-collapse border border-slate-200 text-sm`}>
            <thead>
              {showCombinedColumns ? (
                <>
                  <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <th rowSpan={2} className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-left font-semibold">Priority</th>
                    <th colSpan={visibleStoreColumns.length} className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-center font-semibold">Published</th>
                    <th colSpan={visibleStoreColumns.length} className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-center font-semibold">Out of Stock</th>
                    <th colSpan={visibleStoreColumns.length} className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-center font-semibold">Out of Stock %</th>
                  </tr>
                  <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    {['published', 'out-of-stock', 'out-of-stock-pct'].flatMap((groupKey) => (
                      visibleStoreColumns.map((column) => (
                        <th
                          key={`${groupKey}-${column.alias}`}
                          className="sticky top-[42px] z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold"
                        >
                          {column.shortLabel}
                        </th>
                      ))
                    ))}
                  </tr>
                </>
              ) : (
                <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <th className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-left font-semibold">Priority</th>
                  <th className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">
                    Published ({visibleStoreColumns[0]?.shortLabel || 'Store'})
                  </th>
                  <th className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">
                    Out of Stock ({visibleStoreColumns[0]?.shortLabel || 'Store'})
                  </th>
                  <th className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">
                    Out of Stock % ({visibleStoreColumns[0]?.shortLabel || 'Store'})
                  </th>
                </tr>
              )}
            </thead>
            <tbody className="bg-white">
              {table1Rows.map((row) => (
                <tr key={row.priority} className={row.isTotal ? 'bg-amber-50/70' : ''}>
                  <td className={`border border-slate-200 px-4 py-3 ${row.isTotal ? 'font-bold text-slate-900' : 'font-semibold text-slate-900'}`}>{row.priority}</td>
                  {visibleStoreColumns.map((column) => (
                    <td key={`${row.priority}-${column.alias}-published`} className={`border border-slate-200 px-4 py-3 text-right ${row.isTotal ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{formatNumber(row[column.rowKey]?.totalPublished)}</td>
                  ))}
                  {visibleStoreColumns.map((column) => (
                    <td key={`${row.priority}-${column.alias}-outofstock`} className={`border border-slate-200 px-4 py-3 text-right ${row.isTotal ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{formatNumber(row[column.rowKey]?.outOfStock)}</td>
                  ))}
                  {visibleStoreColumns.map((column) => (
                    <td key={`${row.priority}-${column.alias}-outofstockpct`} className={`border border-slate-200 px-4 py-3 text-right ${row.isTotal ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{formatNumber(row[column.rowKey]?.outOfStockPct)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid w-full gap-4 xl:grid-cols-2">
      <div className="card h-full">
        <h2 className="mb-3 text-base font-semibold text-slate-900">KPI Movement</h2>
        <div
          className="w-full overflow-x-auto overflow-y-visible"
          data-final-report-scroll-shell="true"
          role="region"
          tabIndex={0}
          aria-label="KPI Movement table"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
        >
          <table
            className="w-max border-collapse border border-slate-200 text-sm"
            style={{ minWidth: `${metricsTableMinWidth}px` }}
          >
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="sticky left-0 top-0 z-40 border border-slate-200 bg-slate-50 px-4 py-3 font-semibold">Particular</th>
                {visibleStoreColumns.map((column) => (
                  <th key={`table2-${column.alias}`} className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">{column.label}</th>
                ))}
                {showCombinedColumns ? (
                  <th className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">Total</th>
                ) : null}
              </tr>
            </thead>
            <tbody className="bg-white">
              {table2Rows.map((row) => (
                <tr key={row.particular}>
                  <td className="sticky left-0 z-20 border border-slate-200 bg-white px-4 py-3 font-medium leading-5 text-slate-800">{row.particular}</td>
                  {visibleStoreColumns.map((column) => (
                    <td key={`${row.particular}-${column.alias}`} className="border border-slate-200 px-4 py-3 text-right text-slate-700">{formatNumber(row[column.rowKey])}</td>
                  ))}
                  {showCombinedColumns ? (
                    <td className="border border-slate-200 px-4 py-3 text-right font-semibold text-slate-900">{formatNumber(row.total)}</td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card h-full">
        <h2 className="mb-3 text-base font-semibold text-slate-900">Fruits and Vegetable Related Onemart</h2>
        <div
          className="w-full overflow-x-auto overflow-y-visible"
          data-final-report-scroll-shell="true"
          role="region"
          tabIndex={0}
          aria-label="Fruits and Vegetable Related Onemart table"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
        >
          <table
            className="w-max border-collapse border border-slate-200 text-sm"
            style={{ minWidth: `${metricsTableMinWidth}px` }}
          >
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="sticky left-0 top-0 z-40 border border-slate-200 bg-slate-50 px-4 py-3 font-semibold">Particular</th>
                {visibleStoreColumns.map((column) => (
                  <th key={`table3-${column.alias}`} className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">{column.label}</th>
                ))}
                {showCombinedColumns ? (
                  <th className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">Total</th>
                ) : null}
              </tr>
            </thead>
            <tbody className="bg-white">
              {fruitsRows.map((row) => (
                <tr key={row.particular}>
                  <td className="sticky left-0 z-20 border border-slate-200 bg-white px-4 py-3 font-medium leading-5 text-slate-800">{row.particular}</td>
                  {visibleStoreColumns.map((column) => (
                    <td key={`${row.particular}-${column.alias}`} className="border border-slate-200 px-4 py-3 text-right text-slate-700">{formatNumber(row[column.rowKey])}</td>
                  ))}
                  {showCombinedColumns ? (
                    <td className="border border-slate-200 px-4 py-3 text-right font-semibold text-slate-900">{formatNumber(row.total)}</td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      <div className="card">
        <h2 className="mb-3 text-base font-semibold text-slate-900">Ordered vs Arrived by Priority</h2>
        <div {...getTableShellProps('table4')}>
          <table className={`${showCombinedColumns ? 'min-w-[760px]' : 'min-w-[520px]'} border-collapse border border-slate-200 text-sm`}>
            <thead>
              {showCombinedColumns ? (
                <>
                  <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <th rowSpan={2} className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-left font-semibold">Item Priority</th>
                    {visibleStoreColumns.map((column) => (
                      <th key={`table4-group-${column.alias}`} colSpan={2} className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-center font-semibold">{column.shortLabel}</th>
                    ))}
                  </tr>
                  <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    {visibleStoreColumns.flatMap((column) => ([
                      <th key={`table4-${column.alias}-ordered`} className="sticky top-[42px] z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">SKUs Ordered</th>,
                      <th key={`table4-${column.alias}-arrived`} className="sticky top-[42px] z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">SKUs Arrived</th>,
                    ]))}
                  </tr>
                </>
              ) : (
                <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <th className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-left font-semibold">Item Priority</th>
                  <th className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">
                    SKUs Ordered ({visibleStoreColumns[0]?.shortLabel || 'Store'})
                  </th>
                  <th className="sticky top-0 z-30 border border-slate-200 bg-slate-50 px-4 py-3 text-right font-semibold">
                    SKUs Arrived ({visibleStoreColumns[0]?.shortLabel || 'Store'})
                  </th>
                </tr>
              )}
            </thead>
            <tbody className="bg-white">
              {table3Rows.map((row) => (
                <tr key={row.itemPriority} className={row.isTotal ? 'bg-amber-50/70' : ''}>
                  <td className={`border border-slate-200 px-4 py-3 ${row.isTotal ? 'font-bold text-slate-900' : 'font-medium text-slate-800'}`}>{row.itemPriority}</td>
                  {visibleStoreColumns.flatMap((column) => ([
                    <td key={`${row.itemPriority}-${column.alias}-ordered`} className={`border border-slate-200 px-4 py-3 text-right ${row.isTotal ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{formatNumber(row[column.orderedKey])}</td>,
                    <td key={`${row.itemPriority}-${column.alias}-arrived`} className={`border border-slate-200 px-4 py-3 text-right ${row.isTotal ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{formatNumber(row[column.arrivedKey])}</td>,
                  ]))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && !report ? (
        <div className="card px-4 py-10 text-center text-sm text-slate-500">
          <FileSearch className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3">No final report data found for the selected date.</p>
        </div>
      ) : null}

      {loading ? (
        <div className="card px-4 py-10 text-center text-sm text-slate-500">
          Loading final report...
        </div>
      ) : null}
    </div>
  );
};

export default FinalReport;
